import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Download, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { getImportableSensors, importSensorsToAccount } from '../api';
import {
  normalizeSensorStatus,
  SENSOR_STATUS_CODE_ENTRIES,
} from '../lib/sensorStatus';

interface ImportSensorsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
}

type SortKey = 'name' | 'status' | 'value';
type SortDir = 1 | -1;
const PAGE_SIZE = 50;

export default function ImportSensorsModal({ open, onOpenChange, accountId, accountName }: ImportSensorsModalProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(1);
  const [page, setPage] = useState(1);

  const { data: all, isLoading } = useQuery({
    queryKey: ['importable-sensors', accountId],
    queryFn: () => getImportableSensors(accountId),
    enabled: open,
  });

  const importable = useMemo(() => (all || []).filter((s) => !s.alreadyImported), [all]);

  const filtered = useMemo(() => {
    let result = importable;
    if (statusFilter !== 'all') {
      result = result.filter((s) => s.status === Number(statusFilter));
    }
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter((s) =>
        s.name.toLowerCase().includes(lower) ||
        (s.streamName || '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [importable, search, statusFilter]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(1); }
    setPage(1);
  };

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'status': cmp = a.status - b.status; break;
        case 'value': cmp = (a.value ?? -Infinity) - (b.value ?? -Infinity); break;
      }
      return cmp * sortDir;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const importMutation = useMutation({
    mutationFn: (ids: string[]) => importSensorsToAccount(accountId, ids),
    onSuccess: (result) => {
      const total = result.imported + result.reassigned;
      toast.success(`Imported ${total} sensor${total !== 1 ? 's' : ''}`);
      if (result.errors.length > 0) toast.warning(`${result.errors.length} sensor(s) had errors`, { description: result.errors.slice(0, 3).join('\n') });
      queryClient.invalidateQueries({ queryKey: ['account-sensors', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      queryClient.invalidateQueries({ queryKey: ['importable-sensors', accountId] });
      setSelected(new Set());
      onOpenChange(false);
    },
    onError: () => toast.error('Failed to import sensors'),
  });

  const toggleAll = () => setSelected(selected.size === sorted.length ? new Set() : new Set(sorted.map((s) => s._id)));
  const toggleOne = (id: string) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 text-muted-foreground/50" />;
    return sortDir === 1 ? <ArrowUp className="ml-1 inline h-3.5 w-3.5" /> : <ArrowDown className="ml-1 inline h-3.5 w-3.5" />;
  };

  const resetState = () => { setSelected(new Set()); setSearch(''); setStatusFilter('all'); setSortKey(null); setSortDir(1); setPage(1); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Sensors</DialogTitle>
          <DialogDescription>Select sensors from the legacy organisation to import into <span className="font-medium text-foreground">{accountName}</span></DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 px-1">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input placeholder="Search by name or stream..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="h-8" />
          {search && <button onClick={() => { setSearch(''); setPage(1); }} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-[150px] shrink-0">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {SENSOR_STATUS_CODE_ENTRIES.map(([code, { label }]) => (
                <SelectItem key={code} value={String(code)}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={sorted.length > 0 && selected.size === sorted.length} onCheckedChange={toggleAll} disabled={sorted.length === 0} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')}>Name<SortIcon col="name" /></TableHead>
                <TableHead>Stream</TableHead>
                <TableHead>Legacy Org ID</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" /></TableHead>
                <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort('value')}>Value<SortIcon col="value" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
              ))}
              {!isLoading && sorted.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  {importable.length === 0 ? 'All sensors from this organisation are already imported' : 'No matching sensors'}
                </TableCell></TableRow>
              )}
              {!isLoading && paged.map((s) => {
                const info = normalizeSensorStatus(s.status);
                return (
                  <TableRow key={s._id} className="cursor-pointer hover:bg-muted/50" onClick={() => toggleOne(s._id)}>
                    <TableCell><Checkbox checked={selected.has(s._id)} onCheckedChange={() => toggleOne(s._id)} /></TableCell>
                    <TableCell className="font-medium max-w-[200px] truncate">{s.name}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{s.streamName || '—'}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{s.organisationId || '—'}</TableCell>
                    <TableCell><Badge variant={info.variant}>{info.label}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{s.value != null ? s.value : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <p className="text-sm text-muted-foreground">{selected.size} of {importable.length} selected</p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p - 1)} disabled={safePage <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-2">{safePage} / {totalPages}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p + 1)} disabled={safePage >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Button onClick={() => importMutation.mutate(Array.from(selected))} disabled={selected.size === 0 || importMutation.isPending}>
            {importMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            {importMutation.isPending ? 'Importing...' : `Import ${selected.size} Sensor${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
